import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { DataTable, DataTableColumn } from '@/components/common/DataTable'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { SpinnerIcon } from '@/components/ui/SpinnerIcon'
import { useAdminUsers } from '@/hooks/admin/useAdminUsers'
import { SUBSCRIPTION_PLAN_LABELS } from '@/constants/adminLabels'
import type { AdminUserListItem } from '@/types/admin'

function formatDate(iso: string | null): string {
  if (!iso) return '–'
  return format(new Date(iso), 'yyyy-MM-dd')
}

const columns: DataTableColumn<AdminUserListItem>[] = [
  {
    key: 'email',
    header: 'E-post',
    sortable: true,
    cell: (value, row) => (
      <div className="flex items-center gap-2">
        <span>{value}</span>
        {row.disabled && <Badge variant="destructive">Avstängd</Badge>}
      </div>
    ),
  },
  { key: 'companyName', header: 'Företag', sortable: true },
  { key: 'organizationNumber', header: 'Org.nr' },
  {
    key: 'createdAt',
    header: 'Skapad',
    sortable: true,
    cell: (value) => formatDate(value),
  },
  {
    key: 'lastSignInAt',
    header: 'Senast inloggad',
    sortable: true,
    cell: (value) => formatDate(value),
  },
  {
    key: 'profileCompleted',
    header: 'Profil',
    cell: (value: boolean) => (
      <Badge variant={value ? 'default' : 'secondary'}>
        {value ? 'Komplett' : 'Ofullständig'}
      </Badge>
    ),
  },
  {
    key: 'subscription',
    header: 'Plan',
    cell: (_, row) =>
      row.subscription ? (
        <Badge variant="outline">
          {SUBSCRIPTION_PLAN_LABELS[row.subscription.plan]}
        </Badge>
      ) : (
        <span className="text-muted-foreground">–</span>
      ),
  },
]

export function AdminUsersPage() {
  const navigate = useNavigate()
  const { users, loading, error } = useAdminUsers()
  const [search, setSearch] = useState('')

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Administration – Användare</h1>
        <p className="text-muted-foreground">
          {users.length} användare registrerade
        </p>
      </div>

      <Input
        placeholder="Sök på e-post eller företag..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-sm"
      />

      {loading ? (
        <div className="flex justify-center py-12">
          <SpinnerIcon size="lg" className="text-blue-600" />
        </div>
      ) : error ? (
        <p className="text-destructive py-8">{error}</p>
      ) : (
        <DataTable
          data={users}
          columns={columns}
          searchValue={search}
          onRowClick={(row) => navigate(`/admin/users/${row.uid}`)}
          getRowId={(row) => row.uid}
          emptyMessage="Inga användare hittades"
        />
      )}
    </div>
  )
}
